<script setup lang="tsx">
import { ref, onMounted, computed } from 'vue'
import type { Response } from '@/models/api-generated'
import http from '@/services/http'
import AssetTable from '@/components/asset/AssetTable.vue'
import DataAssetTable from '@/components/data_asset/DataAssetTable.vue'
import { genRouteID } from '@/services/utils'
import { TypeAsset, TypeDataAsset } from '@/models/enums-generated'
import Modal from '../shared/Modal.vue'
import { assetRefreshCallback, dataAssetRefreshCallback } from '@/components/business_processes/shared'
import toastStore from '@/stores/toast'
import { useI18n } from 'vue-i18n'

const isModalVisible = ref(false)

const props = defineProps<{
	bpId: number
}>()

const { t } = useI18n()
const toaster = toastStore()
const tableType = ref<string>('')
const isAsset = computed(() => tableType.value === TypeAsset)
const isDataAsset = computed(() => tableType.value === TypeDataAsset)
const selected = ref<number[]>([])

function checked(id: number) {
	if (!selected.value.includes(id)) selected.value.push(id)
}

function unchecked(id: number) {
	const i = selected.value.indexOf(id)
	if (i >= 0) selected.value.splice(i, 1)
}

function handleSubmit() {
	let endpoint = ''
	const bpId = genRouteID(props.bpId, '', 'BusinessProcess')
	const type = isAsset.value ? 'Asset' : 'DataAsset'
	let selection: string[] = []

	if (isAsset.value) {
		if (!selected.value.length) return
		endpoint = `/api/bp/addassets/${bpId}`
		selection = selected.value.map((a) => genRouteID(a, '', type))
	} else if (isDataAsset.value) {
		if (!selected.value.length) return
		endpoint = `/api/bp/adddataassets/${bpId}`
		selection = selected.value.map((a) => genRouteID(a, '', type))
	}

	http.post<Response<string[]>>(endpoint, { selection }).then(({ data }) => {
		if (data.status === 'OK') {
			if (isAsset.value) {
				assetRefreshCallback.value?.forEach((fn) => fn())
			} else if (isDataAsset.value) {
				dataAssetRefreshCallback.value?.forEach((fn) => fn())
			}
			selected.value = []
			isModalVisible.value = false
			toaster.showSuccess(t('misc.successfullyEdited'))
		}
	})
}

onMounted(async () => {
	const btnAssets = document.getElementById('add-bp-assets')
	const btnDataAssets = document.getElementById('add-bp-dataassets')

	btnDataAssets?.addEventListener('click', () => {
		selected.value = []
		tableType.value = TypeDataAsset
		isModalVisible.value = true
	})
	btnAssets?.addEventListener('click', () => {
		selected.value = []
		tableType.value = TypeAsset
		isModalVisible.value = true
	})
})
</script>

<template>
	<Modal class="modal-lg" v-model="isModalVisible">
		<template #header>
			<template v-if="isAsset">{{ $t('bp.assetLabel') }}</template>
			<template v-if="isDataAsset">{{ $t('bp.dataAssetLabel') }}</template>
		</template>
		<template #body>
			<template v-if="isDataAsset">
				<DataAssetTable
					component="bpconn_data_assets"
					:bpId="props.bpId"
					:bpAdd="true"
					:disableRowClick="true"
					@checked="checked"
					@unchecked="unchecked"
				></DataAssetTable>
			</template>
			<template v-if="isAsset">
				<AssetTable
					component="bpconn_assets"
					:bpId="props.bpId"
					:bpAdd="true"
					:disableRowClick="true"
					@checked="checked"
					@unchecked="unchecked"
				></AssetTable>
			</template>
		</template>
		<template #footer>
			<button id="cancel-asset-connection-btn" type="button" class="btn btn-secondary" data-bs-dismiss="modal">
				{{ $t('misc.cancel') }}
			</button>
			<button
				id="complete-asset-connection-btn"
				type="button"
				class="btn btn-primary"
				:disabled="selected.length === 0"
				@click="handleSubmit"
			>
				{{ $t('misc.add') }}
			</button>
		</template>
	</Modal>
</template>
